import React, { useState, useEffect, useCallback } from 'react';
import TopBar from './components/TopBar';
import LeftSidebar from './components/LeftSidebar';
import MapPanel from './components/MapPanel';
import GroupedAnomalies from './components/GroupedAnomalies';
import BottomPanel from './components/BottomPanel';
import { loadTrialDataWithFallback, getAvailableTrialTypes } from './data/sqlDataLoader';

function App() {
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [overviewMode, setOverviewMode] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: '2023-08-01',
    end: '2023-08-31'
  });
  const [filters, setFilters] = useState({
    mode: 'all',
    status: 'all',
    anomalies: 'all'
  });
  
  // Trial data state
  const [shipments, setShipments] = useState([]);
  const [anomalies, setAnomalies] = useState({});
  const [sensorData, setSensorData] = useState({});
  const [selectedTrialType, setSelectedTrialType] = useState('NestleTrial1');
  const [availableTrialTypes, setAvailableTrialTypes] = useState(['TRIAL-DEFAULT']);
  const [isLoading, setIsLoading] = useState(true);
  const [dataSource, setDataSource] = useState('trial'); // 'trial' or 'mock'

  // Load data function with useCallback to prevent infinite re-renders
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      let data;
      
      data = await loadTrialDataWithFallback(selectedTrialType);
      
      setShipments(data.shipments);
      setAnomalies(data.anomalies);
      setSensorData(data.sensorData);
      
      // Set first shipment as selected if none is selected
      if (!selectedShipment && data.shipments.length > 0) {
        setSelectedShipment(data.shipments[0]);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      // No fallback - just set empty data
      setShipments([]);
      setAnomalies({});
      setSensorData({});
    } finally {
      setIsLoading(false);
    }
  }, [selectedTrialType, selectedShipment]);

  // Load data on component mount and when trial type changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load available trial types on component mount
  useEffect(() => {
    const loadTrialTypes = async () => {
      try {
        const trialTypes = await getAvailableTrialTypes();
        setAvailableTrialTypes(trialTypes);
      } catch (error) {
        // Error handling
      }
    };
    
    loadTrialTypes();
  }, []);

  const handleShipmentSelect = (shipment) => {
    setSelectedShipment(shipment);
    setOverviewMode(false); // Exit overview mode when selecting a specific shipment
  };

  const handleExport = (type) => {
    // Export functionality placeholder
  };

  const handleOverviewToggle = () => {
    setOverviewMode(!overviewMode);
  };

  const handleTrialTypeChange = (trialType) => {
    setSelectedTrialType(trialType);
  };

  const handleDataSourceChange = (source) => {
    setDataSource(source);
  };

  const filteredShipments = shipments.filter(shipment => {
    if (filters.mode !== 'all' && shipment.mode !== filters.mode) return false;
    if (filters.status !== 'all' && shipment.status !== filters.status) return false;
    if (filters.anomalies !== 'all') {
      const anomalyCount = anomalies[shipment.id]?.length || 0;
      if (filters.anomalies === 'with' && anomalyCount === 0) return false;
      if (filters.anomalies === 'without' && anomalyCount > 0) return false;
    }
    return true;
  });



  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading trial data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <TopBar 
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        onExport={handleExport}
        overviewMode={overviewMode}
        onOverviewToggle={handleOverviewToggle}
        dataSource={dataSource}
        onDataSourceChange={handleDataSourceChange}
        selectedTrialType={selectedTrialType}
        availableTrialTypes={availableTrialTypes}
        onTrialTypeChange={handleTrialTypeChange}
      />
      
      <div className="flex-1 flex">
        <LeftSidebar 
          shipments={filteredShipments}
          selectedShipment={selectedShipment}
          onShipmentSelect={handleShipmentSelect}
          filters={filters}
          onFiltersChange={setFilters}
          overviewMode={overviewMode}
          dataSource={dataSource}
          selectedTrialType={selectedTrialType}
        />
        
        <div className="flex-1 flex flex-col">
          <MapPanel 
            shipment={overviewMode ? null : selectedShipment}
            shipments={overviewMode ? filteredShipments : null}
            anomalies={overviewMode ? null : (anomalies[selectedShipment?.id] || [])}
            allAnomalies={overviewMode ? anomalies : null}
            overviewMode={overviewMode}
          />
          

          
          <BottomPanel 
            shipment={selectedShipment}
            sensorData={sensorData[selectedShipment?.id]}
            anomalies={anomalies[selectedShipment?.id] || []}
            overviewMode={overviewMode}
          />
        </div>
        
        <GroupedAnomalies 
          anomalies={anomalies[selectedShipment?.id] || []}
          shipment={selectedShipment}
        />
      </div>
    </div>
  );
}

export default App; 